import { Loader, Title } from 'Mri.Web.Common';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { Alert, Badge, Button, Table } from 'reactstrap';
import { useStore } from '../hooks/use-store';
import { SharedPackage } from './SmartVuMetadataStore';

/**
 * What is actually running: the commits and package versions the deployed software was built from.
 *
 * The Mri.* dependencies are listed separately from the web repository on purpose. The shared ones
 * carry most of SmartVu's behaviour but are consumed as NuGet packages, so the web commit alone does
 * not tell you which version of them is deployed -- which is exactly how an alpha.6 build can be
 * mistaken for one carrying an alpha.7 fix.
 *
 * Two versions are shown per dependency because they disagree in practice: the version the build
 * RESTORED and the version smartvu-shared baked inside the dll. The restored one is the answer to
 * "which alpha are we running"; a mismatch means the shared repo packed a release without bumping
 * its own informational version, and is flagged rather than hidden.
 */
export const SmartVuMetadata = observer(() => {
    const { smartVuMetadataStore } = useStore();

    useEffect(() => {
        smartVuMetadataStore.loadAsync();
    }, [smartVuMetadataStore]);

    const metadata = smartVuMetadataStore.metadata;

    const commitLink = (commit?: string, url?: string) => {
        if (!commit) return <span className="text-muted">not stamped at build time</span>;
        const short = commit.length > 8 ? commit.substring(0, 8) : commit;
        if (!url) return <code>{short}</code>;
        return (
            <a href={url} target="_blank" rel="noopener noreferrer">
                <code>{short}</code>
            </a>
        );
    };

    const orDash = (value?: string) => (value ? <span>{value}</span> : <span className="text-muted">—</span>);

    const utc = (value?: string) => {
        if (!value) return <span className="text-muted">—</span>;
        const parsed = new Date(value);
        return isNaN(parsed.valueOf()) ? <span>{value}</span> : <span>{parsed.toISOString().replace('T', ' ').substring(0, 19)} UTC</span>;
    };

    // The deployment environment (dev/qa/stg/prd) is what people mean by "which environment is this".
    // ASPNETCORE_ENVIRONMENT is shown next to it rather than as it: dev, qa and stg all run as
    // "Staging" because they load the same appsettings.Staging.json, so on its own it said "Staging"
    // on three different environments.
    const environmentLabel = (deployEnvironment?: string) => {
        if (!deployEnvironment) return <span className="text-muted">—</span>;
        const names: { [key: string]: string } = { dev: 'Development', qa: 'QA', stg: 'Staging', prd: 'Production' };
        const name = names[deployEnvironment.toLowerCase()];
        return <span>{name ? `${name} (${deployEnvironment})` : deployEnvironment}</span>;
    };

    return (
        <div className="container-fluid p-3">
            <Title>SmartVu Metadata</Title>

            <div className="d-flex align-items-center mb-3">
                <p className="mb-0 mr-auto text-muted">
                    The commits and package versions the running software was built from.
                </p>
                <Button color="secondary" onClick={() => smartVuMetadataStore.loadAsync()} disabled={smartVuMetadataStore.loading}>
                    Refresh
                </Button>
            </div>

            <Loader loading={smartVuMetadataStore.loading} center showSpinner message="Reading build metadata…" />

            {smartVuMetadataStore.error && <Alert color="danger">{smartVuMetadataStore.error}</Alert>}

            {!smartVuMetadataStore.loading && metadata && (
                <>
                    <h5>Web application</h5>

                    {metadata.web && metadata.web.stamped === false && (
                        <Alert color="warning">
                            {metadata.web.notStampedReason ?? 'No build provenance was stamped into this image.'}
                        </Alert>
                    )}

                    <Table size="sm" bordered responsive className="mb-4">
                        <tbody>
                            <tr>
                                <th style={{ width: '18rem' }}>Repository</th>
                                <td>{orDash(metadata.web?.repository)}</td>
                            </tr>
                            <tr>
                                <th>Commit</th>
                                <td>{commitLink(metadata.web?.commit, metadata.web?.commitUrl)}</td>
                            </tr>
                            <tr>
                                <th>Branch</th>
                                <td>{orDash(metadata.web?.branch)}</td>
                            </tr>
                            <tr>
                                <th>Pipeline</th>
                                <td>
                                    {metadata.web?.pipelineUrl && metadata.web?.pipelineId ? (
                                        <a href={metadata.web.pipelineUrl} target="_blank" rel="noopener noreferrer">
                                            {metadata.web.pipelineId}
                                        </a>
                                    ) : (
                                        orDash(metadata.web?.pipelineId)
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <th>Built</th>
                                <td>{utc(metadata.web?.builtAtUtc)}</td>
                            </tr>
                            <tr>
                                <th>smartvu-shared version</th>
                                <td>
                                    {metadata.sharedPackageVersion ? (
                                        <code>{metadata.sharedPackageVersion}</code>
                                    ) : (
                                        <span className="text-muted">not stamped at build time</span>
                                    )}
                                    <small className="text-muted d-block">
                                        MriSharedPackageVersion — the Mri.* package version this build asked for.
                                    </small>
                                </td>
                            </tr>
                            <tr>
                                <th>Assembly version</th>
                                <td>
                                    {orDash(metadata.web?.assemblyVersion)}
                                    {metadata.web?.informationalVersion &&
                                        metadata.web.informationalVersion !== metadata.web.assemblyVersion && (
                                            <small className="text-muted d-block">
                                                informational: <code>{metadata.web.informationalVersion}</code>
                                            </small>
                                        )}
                                </td>
                            </tr>
                        </tbody>
                    </Table>

                    <h5>Shared dependencies (Mri.*)</h5>
                    <p className="text-muted">
                        Packages are restored from smartvu-shared and move independently of the web commit above.
                        Projects are built from this repository, at that same commit. <em>Restored</em> is the version
                        this build actually resolved; <em>in assembly</em> is the version stamped inside the dll, which
                        lags when smartvu-shared packs a release without bumping it.
                    </p>
                    <Table size="sm" bordered responsive className="mb-4">
                        <thead>
                            <tr>
                                <th style={{ width: '18rem' }}>Dependency</th>
                                <th>Source</th>
                                <th>Restored</th>
                                <th>In assembly</th>
                                <th>Commit</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(metadata.sharedPackages ?? []).map((p: SharedPackage) => (
                                <tr key={p.name}>
                                    <td>
                                        {p.name}
                                        {p.loaded === false && (
                                            <small className="text-muted d-block">not loaded by this process yet</small>
                                        )}
                                    </td>
                                    <td>
                                        {p.source === 'project' ? (
                                            <Badge color="secondary">in-repo project</Badge>
                                        ) : (
                                            <Badge color="info">NuGet package</Badge>
                                        )}
                                    </td>
                                    <td>
                                        <code>{p.packageVersion ?? '—'}</code>
                                    </td>
                                    <td>
                                        <code>{p.assemblyVersion ?? '—'}</code>
                                        {p.versionMismatch && (
                                            <Badge color="warning" className="ml-1" title="The dll reports a different release than the package that was restored.">
                                                mismatch
                                            </Badge>
                                        )}
                                    </td>
                                    <td>{commitLink(p.commit, p.commitUrl)}</td>
                                </tr>
                            ))}
                            {(metadata.sharedPackages ?? []).length === 0 && (
                                <tr>
                                    <td colSpan={5} className="text-muted">
                                        No Mri.* dependencies reported.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    <h5>Deployment</h5>
                    <Table size="sm" bordered responsive>
                        <tbody>
                            <tr>
                                <th style={{ width: '18rem' }}>Environment</th>
                                <td>{environmentLabel(metadata.runtime?.deployEnvironment)}</td>
                            </tr>
                            <tr>
                                <th>Image tag</th>
                                <td>{orDash(metadata.image?.tag)}</td>
                            </tr>
                            <tr>
                                <th>Tier</th>
                                <td>{orDash(metadata.image?.tier)}</td>
                            </tr>
                            <tr>
                                <th>Host</th>
                                <td>{orDash(metadata.runtime?.machineName)}</td>
                            </tr>
                            <tr>
                                <th>Process started</th>
                                <td>{utc(metadata.runtime?.processStartedAtUtc)}</td>
                            </tr>
                            <tr>
                                <th>ASP.NET environment</th>
                                <td>
                                    {orDash(metadata.runtime?.aspNetCoreEnvironment)}
                                    <small className="text-muted d-block">
                                        Which appsettings file is loaded. dev, qa and stg all run as “Staging”.
                                    </small>
                                </td>
                            </tr>
                            <tr>
                                <th>.NET runtime</th>
                                <td>{orDash(metadata.runtime?.frameworkVersion)}</td>
                            </tr>
                        </tbody>
                    </Table>
                </>
            )}
        </div>
    );
});
